/**
 * ChatMessage — one turn in a transcript: who said it, and what.
 *
 * The user's text is shown as typed (``*`` stays ``*``); the assistant's
 * is markdown. A reply that is still streaming shows a caret. Every row
 * uses the same avatar column, so tool calls, the thinking indicator and
 * replies line up under one another.
 *
 * Used by the chat (HomePage) and by a background task's transcript.
 */
import { cn } from "@/lib/cn";
import { DysonSphere, type DysonMode } from "@/components/ui/DysonSphere";
import { CopyAction } from "./CopyAction";
import { Markdown } from "./Markdown";

export type ChatRole = "user" | "assistant";

/** Width of the avatar column plus its gap: indent for rows without one. */
export const CHAT_INDENT = "pl-9";

export function ChatAvatar({ role, mode = "idle" }: { role: ChatRole; mode?: DysonMode }) {
  if (role === "assistant") {
    // 24 px renders DysonSphere's flat mini variant — cheap enough for
    // one per reply.
    return (
      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center" aria-hidden>
        <DysonSphere mode={mode} size={24} className="h-full w-full" />
      </span>
    );
  }
  return (
    <span
      className="flex h-6 w-6 flex-shrink-0 items-center justify-center border border-accent bg-accent font-mono text-[9px] text-accent-fg"
      aria-hidden
    >
      you
    </span>
  );
}

export interface ChatMessageProps {
  role: ChatRole;
  text: string;
  /** Still arriving: show a caret, no actions yet. */
  streaming?: boolean;
  /** Narrow surfaces (a task panel) use the full width. */
  wide?: boolean;
  "data-testid"?: string;
}

export function ChatMessage({ role, text, streaming = false, wide = false, ...rest }: ChatMessageProps) {
  const isUser = role === "user";
  return (
    <div className={cn("group flex w-full items-start gap-3", isUser && "flex-row-reverse")}>
      <ChatAvatar role={role} mode={streaming ? "speaking" : "idle"} />
      <div className={cn("flex min-w-0 flex-col gap-1", wide ? "max-w-full flex-1" : "max-w-[72%]", isUser && "items-end")}>
        <div
          data-testid={rest["data-testid"]}
          className={cn(
            "border px-4 py-3 text-sm leading-relaxed text-fg",
            isUser ? "border-accent bg-accent/10" : "border-border bg-bg-elevated",
          )}
        >
          {isUser ? (
            <span className="whitespace-pre-wrap">{text}</span>
          ) : streaming ? (
            // Raw text while streaming: half a markdown table or list would
            // reflow on every delta.
            <span className="whitespace-pre-wrap" data-testid="streaming">
              {text}
              <span className="ml-0.5 inline-block h-3.5 w-px animate-pulse bg-accent align-middle" />
            </span>
          ) : (
            <Markdown text={text} />
          )}
        </div>
        {!isUser && !streaming && text && (
          <div className="opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <CopyAction text={text} ariaLabel="Copy reply" />
          </div>
        )}
      </div>
    </div>
  );
}

export function ChatError({ message }: { message: string }) {
  return (
    <div role="alert" className="border border-danger/50 bg-danger/5 px-3 py-2 text-sm text-danger whitespace-pre-wrap">
      {message}
    </div>
  );
}
